import _sortBy from 'lodash/sortBy';

import TYPES from '../action-types';
import * as helpers from '../helpers/productAttributes';

const matchesRemoveTarget = helpers.matchesProductAttribute;

const fakeItems = [
  { isTmp: true, title: '1' },
  { isTmp: true, title: '2' },
  { isTmp: true, title: '3' },
  { isTmp: true, title: '4' },
  { isTmp: true, title: '5' },
];

const buildGroups = (items) => {
  const scopes = ['product', 'variant'];

  return scopes.flatMap((scope) => {
    const scopeItems = _sortBy(
      items.filter((item) => item.scope === scope),
      ['order'],
    );

    if (!scopeItems.length) return [];

    return helpers.generateGroups(scopeItems, helpers.GROUP_TITLES_BY_SCOPE[scope]);
  });
};

const defaultState = {
  isLoaded: false,
  isAdding: false,
  items: [],
  groups: [],
  filtredGroups: null,
  error: null,
  addError: null,
  searchQuery: '',
  schemaWarnings: [],
};

export default function (state = defaultState, action) {
  const { type, payload, error } = action;

  switch (type) {
  case TYPES.PRODUCT_ATTRIBUTES.FETCH.START: {
    return {
      ...state,
      isLoaded: false,
      items: [...fakeItems],
      groups: [...fakeItems],
      error: null,
      schemaWarnings: [],
    };
  }
  case TYPES.PRODUCT_ATTRIBUTES.FETCH.COMPLETE: {
    const items = helpers.sortProductAttributes(payload.items);
    return {
      ...state,
      isLoaded: true,
      items,
      groups: buildGroups(items),
      schemaWarnings: payload.schemaWarnings || [],
    };
  }
  case TYPES.PRODUCT_ATTRIBUTES.FETCH.FAILED: {
    return {
      ...state,
      isLoaded: true,
      items: [],
      groups: [],
      error,
      schemaWarnings: [],
    };
  }

  case TYPES.PRODUCT_ATTRIBUTES.ADD.START: {
    const newItem = { ...payload.item, isTmp: true };
    const newItems = [...state.items.map((item) => {
      if (item.scope === newItem.scope && item.order >= newItem.order) {
        return { ...item, order: (item.order || 0) + 1 };
      }
      return item;
    })];
    newItems.push(newItem);

    const sortedItems = helpers.sortProductAttributes(newItems);

    return {
      ...state,
      isAdding: true,
      items: sortedItems,
      groups: buildGroups(sortedItems),
      addError: null,
    };
  }
  case TYPES.PRODUCT_ATTRIBUTES.ADD.COMPLETE: {
    const newItem = payload.item;
    const newItems = [...state.items.map((item) => {
      if (item.order === newItem.order
        && item.scope === newItem.scope
        && item.isTmp) {
        const { isTmp, ...other } = item; // eslint-disable-line
        return { ...other, ...newItem };
      }
      return item;
    })];

    return {
      ...state,
      isAdding: false,
      items: newItems,
      groups: buildGroups(newItems),
    };
  }
  case TYPES.PRODUCT_ATTRIBUTES.ADD.FAILED: {
    const filteredItems = helpers.sortProductAttributes(state.items.filter((item) => {
      if (item.scope !== payload.item.scope) return true;
      if (item.title !== payload.item.title) return true;
      if (item.title === payload.item.title && !item.isTmp) return true;
      return false;
    }));
    return {
      ...state,
      isAdding: false,
      items: filteredItems,
      groups: buildGroups(filteredItems),
      addError: error,
    };
  }

  case TYPES.PRODUCT_ATTRIBUTES.REMOVE_ERROR: {
    return {
      ...state,
      error: null,
      addError: null,
    };
  }

  case TYPES.PRODUCT_ATTRIBUTES.REMOVE.START: {
    const nextItems = state.items.map((item) => (
      matchesRemoveTarget(item, payload) ? { ...item, isTmp: true } : item
    ));
    return {
      ...state,
      items: nextItems,
      groups: buildGroups(nextItems),
    };
  }
  case TYPES.PRODUCT_ATTRIBUTES.REMOVE.COMPLETE: {
    const nextItems = state.items.filter((item) => !matchesRemoveTarget(item, payload));
    return {
      ...state,
      items: nextItems,
      groups: buildGroups(nextItems),
      filtredGroups: state.filtredGroups
        ? state.filtredGroups.map((group) => group.filter((item) => !matchesRemoveTarget(item, payload)))
        : null,
    };
  }
  case TYPES.PRODUCT_ATTRIBUTES.REMOVE.FAILED: {
    const nextItems = state.items.map((item) => {
      if (matchesRemoveTarget(item, payload)) {
        const { isTmp, ...other } = item; // eslint-disable-line
        return { ...other };
      }
      return item;
    });
    return {
      ...state,
      items: nextItems,
      groups: buildGroups(nextItems),
      error,
    };
  }

  case TYPES.PRODUCT_ATTRIBUTES.UPDATE.START: {
    const { titles, scope } = payload;
    return {
      ...state,
      items: state.items.map((item) => (
        item.scope === scope && titles.includes(item.title)
          ? { ...item, isUpdating: true }
          : item
      )),
    };
  }
  case TYPES.PRODUCT_ATTRIBUTES.UPDATE.COMPLETE: {
    const {
      titles, key, value, scope,
    } = payload;
    const handleField = (field) => {
      if (field.scope === scope && titles.includes(field.title)) {
        const { isUpdating, ...data } = field; // eslint-disable-line
        return { ...data, [key]: value };
      }
      return field;
    };
    const nextItems = state.items.map(handleField);
    return {
      ...state,
      items: nextItems,
      groups: buildGroups(nextItems),
      filtredGroups: state.filtredGroups
        ? state.filtredGroups.map((group) => group.map(handleField))
        : null,
    };
  }
  case TYPES.PRODUCT_ATTRIBUTES.UPDATE.FAILED: {
    const { titles, scope } = payload;
    const nextItems = state.items.map((item) => {
      if (item.scope === scope && titles.includes(item.title)) {
        const { isUpdating, ...data } = item; // eslint-disable-line
        return { ...data };
      }
      return item;
    });
    return {
      ...state,
      items: nextItems,
      groups: buildGroups(nextItems),
    };
  }

  case TYPES.PRODUCT_ATTRIBUTES.UPDATE_ONE.START: {
    const { customField } = payload;
    const nextItems = state.items.map(
      (item) => (
        customField.title === item.title && customField.scope === item.scope
          ? { ...item, isUpdating: true }
          : item
      ),
    );
    return {
      ...state,
      items: nextItems,
      groups: buildGroups(nextItems),
    };
  }
  case TYPES.PRODUCT_ATTRIBUTES.UPDATE_ONE.CANCELLED: {
    const { customField } = payload;
    const nextItems = state.items.map(
      (item) => (
        customField.title === item.title && customField.scope === item.scope
          ? { ...item, isUpdating: false }
          : item
      ),
    );
    return {
      ...state,
      items: nextItems,
      groups: buildGroups(nextItems),
    };
  }
  case TYPES.PRODUCT_ATTRIBUTES.UPDATE_ONE.COMPLETE: {
    const { customField } = payload;
    const handleField = (field) => {
      if (customField.title === field.title && customField.scope === field.scope) {
        const { isUpdating, ...data } = field; // eslint-disable-line
        if (customField.type !== 'enum') {
          delete data.options;
          delete data.multiple;
        }
        return { ...data, ...customField };
      }
      return field;
    };
    const nextItems = state.items.map(handleField);
    return {
      ...state,
      items: nextItems,
      groups: buildGroups(nextItems),
      filtredGroups: state.filtredGroups
        ? state.filtredGroups.map((group) => group.map(handleField))
        : null,
    };
  }
  case TYPES.PRODUCT_ATTRIBUTES.UPDATE_ONE.FAILED: {
    const { customField } = payload;
    const nextItems = state.items.map((item) => {
      if (customField.title === item.title && customField.scope === item.scope) {
        const { isUpdating, ...data } = item; // eslint-disable-line
        return { ...data };
      }
      return item;
    });
    return {
      ...state,
      items: nextItems,
      groups: buildGroups(nextItems),
    };
  }

  case TYPES.PRODUCT_ATTRIBUTES.MOVE.START: {
    const { scope } = payload;
    const nextItems = state.items.map((item) => (
      item.scope === scope ? { ...item, isUpdating: true } : item
    ));
    return {
      ...state,
      items: nextItems,
      groups: buildGroups(nextItems),
    };
  }
  case TYPES.PRODUCT_ATTRIBUTES.MOVE.COMPLETE: {
    const { items: scopeItems, scope } = payload;
    const otherItems = state.items.filter((item) => item.scope !== scope);
    const items = helpers.sortProductAttributes([...otherItems, ...scopeItems]);
    return {
      ...state,
      items,
      groups: buildGroups(items),
      filtredGroups: null,
      searchQuery: '',
    };
  }
  case TYPES.PRODUCT_ATTRIBUTES.MOVE.FAILED: {
    const nextItems = state.items.map((item) => {
      const { isUpdating, ...data } = item; // eslint-disable-line
      return data;
    });
    return {
      ...state,
      items: nextItems,
      groups: buildGroups(nextItems),
      error,
    };
  }

  case TYPES.PRODUCT_ATTRIBUTES.SEARCH: {
    const { value, scope } = payload;
    const scopedItems = helpers.filterItemsByScope(state.items, scope);

    return {
      ...state,
      searchQuery: value,
      items: state.items,
      filtredGroups: value
        ? buildGroups(helpers.makeSearchItems(scopedItems, value))
        : null,
    };
  }

  default: {
    return state;
  }
  }
}
